import { Config } from "interfaces/Configs";
import { Match } from "interfaces/Match";
import { SESSION_STATUS } from "interfaces/Sessions";
import { HeadsetStatus, Spinner, TrashCanIcon } from "ui";
import SessionIcon from "ui/Icons/Session";
import { SESSIONS_COLUMNS } from "utils/table/sessions-table";

import Buttons from "./Buttons";
import Status from "./Status";

interface SessionProps {
  config: Config;
  sessionsData?: {
    sessionId: string;
    matches: Match[];
    status: SESSION_STATUS;
  }[];
  index: number;
  configsFetching: boolean;
  isActionLoading: boolean;
  handleStartSession: (configId: string) => Promise<void>;
  handleDeleteMatch: (matchId: string) => Promise<void>;
  handleStartGame: (sessionId: string) => Promise<void>;
}

const Session = ({
  config,
  sessionsData,
  index,
  configsFetching,
  isActionLoading,
  handleStartSession,
  handleDeleteMatch,
  handleStartGame,
}: SessionProps) => {
  const filter = config.containerSlots.filter((item) => item.slotId);

  const session = sessionsData?.find(
    ({ sessionId }) => sessionId === config.sessions[0]?._id,
  );

  const sessionMatches = session?.matches || [];

  const areHeadsetsDisconnected = sessionMatches.every(
    (match) => match.headset[0].connectionId === "",
  );

  const isSomeSessionInProgress = sessionsData?.some(
    ({ status }) => status === SESSION_STATUS.IN_PROGRESS,
  );

  const hasLowBattery =
    sessionMatches?.some((match) =>
      match.headset.some((headset) => headset.battery <= 10),
    ) || false;

  return (
    <>
      <div
        key={config._id}
        className="max-w-[1115px] rounded-md p-4 flex flex-col gap-4 border-opacity-40 bg-faux-gunmetal max-h-[350px]"
      >
        <div className="w-full flex justify-between">
          <div className="flex items-center">
            <SessionIcon fill={index === 0 ? "#B558F6" : "#470A72"} />
            <p className="font-semibold text-lg ml-2">{`Sesión ${index + 1}`}</p>
          </div>
          {!!filter.length ? (
            session && session.status !== SESSION_STATUS.COMPLETE ? (
              <Status sessionStatus={session.status} />
            ) : (
              "Disponible"
            )
          ) : (
            ""
          )}
        </div>
        {!filter.length ? (
          <p className="flex justify-center mb-4">
            Vincule cascos a esta sesión para comenzar una partida
          </p>
        ) : (
          <>
            <div className="overflow-y-auto">
              <table className="min-w-full divide-y divide-white">
                <thead className="sticky top-0 bg-faux-gunmetal ">
                  <tr className="border-none">
                    {SESSIONS_COLUMNS.map((column, index) => (
                      <th
                        scope="col"
                        className="py-3 text-faux-whirlpool text-center text-xs uppercase tracking-wider dark:text-primary-100"
                        key={index}
                      >
                        <div className="flex flex-row items-center justify-start text-sm">
                          {column.title}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {configsFetching || !sessionsData ? (
                    <tr>
                      <td colSpan={5} className="text-center">
                        <div className="h-20 flex justify-center items-center">
                          <Spinner size={30} />
                        </div>
                      </td>
                    </tr>
                  ) : (
                    sessionMatches?.map((match) => {
                      if (!match._id) return <></>;
                      const {
                        _id,
                        container,
                        headset,
                        headsetId,
                        playerAlias,
                      } = match;
                      const slotNumber = config.containerSlots.find(
                        ({ slotId }) => slotId.deviceId === headsetId,
                      )?.slotId.number;

                      return (
                        <tr className="border-b" key={_id}>
                          <td className="pt-4 pb-2">{playerAlias}</td>
                          <td>{container[0]?.number}</td>
                          <td>{slotNumber}</td>
                          <td>
                            <HeadsetStatus
                              isConnected={!!headset[0]?.connectionId}
                              batteryValue={headset[0].battery}
                            />
                          </td>
                          <td>
                            {session?.status !== SESSION_STATUS.IN_PROGRESS && (
                              <TrashCanIcon
                                onClick={() => handleDeleteMatch(match._id)}
                              />
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            {!configsFetching && !!sessionsData && (
              <Buttons
                {...session}
                configId={config._id}
                disabledStartMatch={
                  isSomeSessionInProgress || areHeadsetsDisconnected
                }
                isLoading={configsFetching || !sessionsData}
                showWarningMsg={
                  sessionMatches.length > 0 && areHeadsetsDisconnected
                }
                isActionLoading={isActionLoading}
                handleStartSession={handleStartSession}
                handleStartGame={handleStartGame}
                hasLowBatteryHeadsets={hasLowBattery}
              />
            )}
          </>
        )}
      </div>
    </>
  );
};

export default Session;
